import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const MAX_FILES = 5;
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB

export default function UploadDocumentsPage({ uploadedFiles, setUploadedFiles, onNext, onBack }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const onDrop = useCallback(async (acceptedFiles) => {
    setError('');

    // Check total file count
    if (uploadedFiles.length + acceptedFiles.length > MAX_FILES) {
      setError(`Maximum ${MAX_FILES} files allowed. You can upload ${MAX_FILES - uploadedFiles.length} more file(s).`);
      return;
    }

    // Upload files
    for (const file of acceptedFiles) {
      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        setError(`File "${file.name}" is too large. Maximum size is 100 MB.`);
        continue;
      }

      // Add file to list with "uploading" status
      const tempId = `temp-${Date.now()}-${Math.random()}`;
      const newFile = {
        id: tempId,
        name: file.name,
        size: file.size,
        status: 'uploading',
        progress: 0
      };

      setUploadedFiles(prev => [...prev, newFile]);

      try {
        // Upload to backend
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(`${API_URL}/api/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadedFiles(prev =>
              prev.map(f =>
                f.id === tempId ? { ...f, progress } : f
              )
            );
          }
        });

        // Update file with real ID and "uploaded" status
        setUploadedFiles(prev =>
          prev.map(f =>
            f.id === tempId
              ? {
                  ...f,
                  id: response.data.file_id,
                  status: 'uploaded',
                  progress: 100
                }
              : f
          )
        );
      } catch (err) {
        console.error('Upload error:', err);
        // Update file status to "error"
        setUploadedFiles(prev =>
          prev.map(f =>
            f.id === tempId
              ? { ...f, status: 'error', error: err.response?.data?.detail || 'Upload failed' }
              : f
          )
        );
        setError(`Failed to upload "${file.name}". Please try again.`);
      }
    }
  }, [uploadedFiles, setUploadedFiles]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    maxFiles: MAX_FILES - uploadedFiles.length,
    disabled: uploadedFiles.length >= MAX_FILES
  });

  const removeFile = async (fileId) => {
    try {
      // Only delete from backend if not a temp ID
      if (!fileId.startsWith('temp-')) {
        await axios.delete(`${API_URL}/api/upload/${fileId}`);
      }
      setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
      setError('');
    } catch (err) {
      console.error('Delete error:', err);
      setError('Failed to remove file. Please try again.');
    }
  };

  const handleContinue = () => {
    const uploadedCount = uploadedFiles.filter(f => f.status === 'uploaded').length;
    if (uploadedCount === 0) {
      setError('Please upload at least one document to continue.');
      return;
    }
    onNext();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const uploadedCount = uploadedFiles.filter(f => f.status === 'uploaded').length;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-border p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-navy mb-2">
          Step 2 of 4: Upload Documents
        </h2>
        <div className="w-32 h-1 bg-primary"></div>
        <p className="text-text-secondary mt-4">
          Upload 1-5 SEC filing documents (10-K, 10-Q, 8-K, DEF 14A, S-1, etc.)
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-error rounded-lg">
          <p className="text-error text-sm">{error}</p>
        </div>
      )}

      {/* Dropzone */}
      {uploadedFiles.length < MAX_FILES && (
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-all ${
            isDragActive
              ? 'border-primary bg-blue-50'
              : 'border-border hover:border-primary hover:bg-gray-50'
          }`}
        >
          <input {...getInputProps()} />
          <div className="space-y-4">
            <div className="text-6xl">📄</div>
            <div>
              {isDragActive ? (
                <p className="text-lg font-medium text-primary">Drop files here...</p>
              ) : (
                <>
                  <p className="text-lg font-medium text-navy mb-2">
                    Drag and drop PDFs here
                  </p>
                  <p className="text-text-secondary">or click to browse</p>
                </>
              )}
            </div>
            <div className="text-sm text-text-secondary space-y-1">
              <p>Upload 1-{MAX_FILES} files</p>
              <p>(10-K, 10-Q, 8-K, DEF 14A, etc.)</p>
              <p>Max 100 MB per file</p>
            </div>
          </div>
        </div>
      )}

      {/* Uploaded Files List */}
      {uploadedFiles.length > 0 && (
        <div className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-navy">
              Uploaded Files ({uploadedCount}/{MAX_FILES})
            </h3>
            {uploadedFiles.length < MAX_FILES && (
              <p className="text-sm text-text-secondary">
                {MAX_FILES - uploadedFiles.length} more allowed
              </p>
            )}
          </div>

          <div className="space-y-3">
            {uploadedFiles.map((file) => (
              <div
                key={file.id}
                className="border border-border rounded-lg p-4 hover:shadow-sm transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">
                        {file.status === 'uploaded' && '✓'}
                        {file.status === 'uploading' && '⏳'}
                        {file.status === 'error' && '❌'}
                      </span>
                      <div className="flex-1">
                        <p className="font-medium text-navy">{file.name}</p>
                        <p className="text-sm text-text-secondary">
                          {formatFileSize(file.size)} •{' '}
                          {file.status === 'uploaded' && 'Uploaded'}
                          {file.status === 'uploading' && 'Uploading...'}
                          {file.status === 'error' && 'Upload failed'}
                        </p>
                        {file.status === 'error' && file.error && (
                          <p className="text-sm text-error mt-1">{file.error}</p>
                        )}
                      </div>
                    </div>

                    {/* Progress Bar */}
                    {file.status === 'uploading' && (
                      <div className="mt-3">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-primary h-2 rounded-full transition-all duration-300"
                            style={{ width: `${file.progress}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-text-secondary mt-1">
                          {file.progress}%
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Remove Button */}
                  <button
                    onClick={() => removeFile(file.id)}
                    className="ml-4 text-error hover:text-red-700 transition-colors"
                    aria-label="Remove file"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-6 mt-6 border-t border-border">
        <button
          onClick={onBack}
          className="px-6 py-3 border border-border text-navy font-medium rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-2"
        >
          <span>←</span>
          <span>Back</span>
        </button>

        <button
          onClick={handleContinue}
          disabled={uploadedCount === 0}
          className="px-8 py-3 bg-primary text-white font-medium rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          <span>Continue</span>
          <span>→</span>
        </button>
      </div>
    </div>
  );
}
